// The game page's loader: starts the Unity WebGL build named by
// window.HEAVENSTEEL_LOADER (inlined by the Worker), shows progress, and offers
// the helpers the Unity jslib calls (window.HEAVENSTEEL_HOST). The page ↔ Unity
// contract (window.HEAVENSTEEL, window.HEAVENSTEEL_STATUS) is in common.md and
// docs/deploy.md; the Worker sets both before this script runs.

interface Variant {
	loaderUrl: string;
	dataUrl: string;
	frameworkUrl: string;
	codeUrl: string;
	symbolsUrl: string | null;
}

interface LoaderConfig extends Variant {
	version: string;
	base: string;
	streamingAssetsUrl: string;
	mobile: Variant | null;
	companyName: string;
	productName: string;
	productVersion: string;
}

interface Status {
	scene: string;
	phase: string | null;
	round: number;
	seat: number | null;
	hp: [number, number];
	result: unknown;
	error: string | null;
	[k: string]: unknown;
}

interface UnityInstance {
	SetFullscreen(on: 0 | 1): void;
	SendMessage(object: string, method: string, arg?: string | number): void;
	Quit(): Promise<void>;
}

type CreateUnityInstance = (
	canvas: HTMLCanvasElement,
	config: Record<string, unknown>,
	onProgress: (p: number) => void,
) => Promise<UnityInstance>;

declare global {
	interface Window {
		HEAVENSTEEL: {
			api: string;
			ws: string;
			room: string | null;
			build: string | null;
			autoplay: boolean;
			name: string | null;
			site: string;
			mode: "queue" | "challenge" | null;
		};
		HEAVENSTEEL_STATUS: Status;
		HEAVENSTEEL_LOADER: LoaderConfig | null;
		HEAVENSTEEL_ENV: string;
		HEAVENSTEEL_HOST: {
			copyText(text: string): Promise<boolean>;
			share(
				title: string,
				url: string,
			): Promise<"shared" | "copied" | "failed">;
			setStatus(patch: Partial<Status>): void;
			toast(message: string): void;
		};
		HEAVENSTEEL_INSTANCE?: UnityInstance;
		createUnityInstance?: CreateUnityInstance;
	}
}

const $ = <T extends HTMLElement>(sel: string) =>
	document.querySelector(sel) as T;

const canvas = $<HTMLCanvasElement>("#unity-canvas");
const loading = $<HTMLElement>("#loading");
const fatal = $<HTMLElement>("#fatal");
const rotate = $<HTMLElement>("#rotate");
const toastEl = $<HTMLElement>("#toast");
const bar = $<HTMLElement>("#loading .bar");
const barFill = $<HTMLElement>("#loading .bar-fill");
const loadingText = $<HTMLElement>("#loading .loading-text");
const loadingPct = $<HTMLElement>("#loading .loading-pct");
const tipEl = $<HTMLElement>("#loading .tip");

const TIPS = [
	"Deployment is hidden: your opponent sees your army only when the battle starts.",
	"From round 2, pick one of four reinforcement cards before you deploy.",
	"Squads earn XP from damage and kills, and keep it between rounds.",
	"Losing a round costs HP: the more of the winner's army survives, the more it costs.",
	"Tech what you have, or buy something new? That's the whole game.",
	"Matches end when a side reaches 0 HP, or after round 20.",
	"Lost the connection? Reload: the match waits 60 seconds for you.",
];

// ── status ──────────────────────────────────────────────────────────────

function setStatus(patch: Partial<Status>): void {
	window.HEAVENSTEEL_STATUS = { ...window.HEAVENSTEEL_STATUS, ...patch };
	window.dispatchEvent(
		new CustomEvent("heavensteel:status", {
			detail: window.HEAVENSTEEL_STATUS,
		}),
	);
}

// ── toast, copy, share (called by the Unity jslib when present) ─────────

let toastTimer = 0;
function toast(message: string): void {
	toastEl.textContent = message;
	toastEl.hidden = false;
	toastEl.classList.add("show");
	window.clearTimeout(toastTimer);
	toastTimer = window.setTimeout(() => {
		toastEl.classList.remove("show");
		window.setTimeout(() => {
			toastEl.hidden = true;
		}, 250);
	}, 2400);
}

function legacyCopy(text: string): boolean {
	const ta = document.createElement("textarea");
	ta.value = text;
	ta.setAttribute("readonly", "");
	ta.style.position = "fixed";
	ta.style.opacity = "0";
	document.body.appendChild(ta);
	ta.select();
	ta.setSelectionRange(0, text.length);
	let ok = false;
	try {
		ok = document.execCommand("copy");
	} catch {
		ok = false;
	}
	ta.remove();
	return ok;
}

async function copyText(text: string): Promise<boolean> {
	let ok = false;
	try {
		if (navigator.clipboard && window.isSecureContext) {
			await navigator.clipboard.writeText(text);
			ok = true;
		}
	} catch {
		ok = false;
	}
	if (!ok) ok = legacyCopy(text);
	toast(ok ? "Link copied" : text);
	return ok;
}

const coarse = window.matchMedia("(pointer: coarse)").matches;

async function share(
	title: string,
	url: string,
): Promise<"shared" | "copied" | "failed"> {
	if (coarse && typeof navigator.share === "function") {
		try {
			await navigator.share({ title, url });
			return "shared";
		} catch (e) {
			if ((e as DOMException)?.name === "AbortError") return "failed";
		}
	}
	return (await copyText(url)) ? "copied" : "failed";
}

window.HEAVENSTEEL_HOST = { copyText, share, setStatus, toast };

// ── overlays ────────────────────────────────────────────────────────────

function showFatal(title: string, text: string, code: string): void {
	setStatus({ error: code });
	loading.hidden = true;
	$<HTMLElement>("#fatal .fatal-title").textContent = title;
	$<HTMLElement>("#fatal .fatal-text").textContent = text;
	fatal.hidden = false;
}

document.addEventListener("click", (e) => {
	const action = (e.target as HTMLElement | null)
		?.closest("[data-action]")
		?.getAttribute("data-action");
	if (action === "reload") window.location.reload();
	if (action === "dismiss-rotate") {
		try {
			sessionStorage.setItem("hs.rotateDismissed", "1");
		} catch {}
		rotate.hidden = true;
	}
});

function updateRotateHint(): void {
	let dismissed = false;
	try {
		dismissed = sessionStorage.getItem("hs.rotateDismissed") === "1";
	} catch {}
	const portrait = window.matchMedia("(orientation: portrait)").matches;
	rotate.hidden = !(coarse && portrait && !dismissed);
}

let progressShown = 0;
function setProgress(p: number): void {
	const pct = Math.max(progressShown, Math.min(100, Math.round(p * 100)));
	progressShown = pct;
	barFill.style.transform = `scaleX(${pct / 100})`;
	bar.setAttribute("aria-valuenow", String(pct));
	loadingPct.textContent = `${pct}%`;
	loadingText.textContent =
		pct < 90 ? "Downloading the battlefield…" : "Starting the engine…";
	setStatus({ progress: pct });
}

let tipIndex = Math.floor(Math.random() * TIPS.length);
function nextTip(): void {
	tipEl.textContent = TIPS[tipIndex % TIPS.length];
	tipIndex++;
}

// ── Unity ───────────────────────────────────────────────────────────────

/** Phones get the ASTC build when there is one and the GPU has no BCn/DXT. */
function pickVariant(cfg: LoaderConfig): Variant {
	if (!cfg.mobile) return cfg;
	const forced = new URLSearchParams(location.search).get("variant");
	if (forced === "mobile") return cfg.mobile;
	if (forced === "desktop") return cfg;
	try {
		const gl = document.createElement("canvas").getContext("webgl2");
		if (!gl) return cfg;
		const s3tc = !!gl.getExtension("WEBGL_compressed_texture_s3tc");
		const astc = !!gl.getExtension("WEBGL_compressed_texture_astc");
		return !s3tc && astc ? cfg.mobile : cfg;
	} catch {
		return cfg;
	}
}

function hasWebGL2(): boolean {
	try {
		return !!document.createElement("canvas").getContext("webgl2");
	} catch {
		return false;
	}
}

function loadScript(src: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const s = document.createElement("script");
		s.src = src;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error(`could not load ${src}`));
		document.head.appendChild(s);
	});
}

async function start(): Promise<void> {
	updateRotateHint();
	window.addEventListener("resize", updateRotateHint);
	nextTip();
	const tipTimer = window.setInterval(nextTip, 6000);

	const cfg = window.HEAVENSTEEL_LOADER;
	if (!cfg) {
		window.clearInterval(tipTimer);
		const local = window.HEAVENSTEEL_ENV === "local";
		showFatal(
			"No game build yet",
			local
				? "The local stack has no WebGL build loaded. Build one (HSWeb.BuildWebGL), then restart with: bun run dev:web --build <apps/unity/Builds/web/<version>>"
				: "The game build hasn't been published yet. Try again in a few minutes.",
			"noBuild",
		);
		return;
	}
	if (!hasWebGL2()) {
		window.clearInterval(tipTimer);
		showFatal(
			"WebGL 2 isn't available",
			"Heavensteel needs WebGL 2. Try a current Chrome, Edge, Firefox or Safari, and check that hardware acceleration is on.",
			"noWebGL2",
		);
		return;
	}

	const v = pickVariant(cfg);
	const params = new URLSearchParams(location.search);
	const dprParam = Number(params.get("dpr"));
	const devicePixelRatio =
		dprParam > 0 && dprParam <= 4 ? dprParam : window.devicePixelRatio || 1;

	canvas.addEventListener("contextmenu", (e) => e.preventDefault());
	canvas.addEventListener("pointerdown", () => canvas.focus());

	setProgress(0);
	try {
		await loadScript(v.loaderUrl);
		const create = window.createUnityInstance;
		if (!create)
			throw new Error("the Unity loader didn't define createUnityInstance");
		const config: Record<string, unknown> = {
			dataUrl: v.dataUrl,
			frameworkUrl: v.frameworkUrl,
			codeUrl: v.codeUrl,
			streamingAssetsUrl: cfg.streamingAssetsUrl,
			companyName: cfg.companyName,
			productName: cfg.productName,
			productVersion: cfg.productVersion,
			matchWebGLToCanvasSize: true,
			devicePixelRatio,
			// Versioned paths never change: skip revalidating Unity's IndexedDB cache.
			cacheControl: (url: string) =>
				url.includes("/builds/") ? "immutable" : "no-store",
			showBanner: (msg: string, type: string) => {
				if (type === "error") {
					console.error("[unity]", msg);
					setStatus({ error: msg });
					toast(msg);
				} else console.warn("[unity]", msg);
			},
		};
		if (v.symbolsUrl) config.symbolsUrl = v.symbolsUrl;
		const instance = await create(canvas, config, setProgress);
		window.HEAVENSTEEL_INSTANCE = instance;
		window.clearInterval(tipTimer);
		setProgress(1);
		loading.classList.add("done");
		window.setTimeout(() => {
			loading.hidden = true;
		}, 400);
		if (window.HEAVENSTEEL_STATUS.scene === "loading")
			setStatus({ scene: "boot" });
		canvas.focus();
	} catch (e) {
		window.clearInterval(tipTimer);
		const msg = e instanceof Error ? e.message : String(e);
		console.error("[heavensteel] load failed", e);
		showFatal(
			"The game couldn't start",
			`${msg}. Reload to try again; if it keeps failing, try another browser.`,
			"loadFailed",
		);
	}
}

void start();

export {};
